import { type CapacityTotals, formatCount, formatDataCapacity, formatPower, formatRate } from "../domain/capacity";
import type { ComponentPerformance } from "../domain/types";
import { useUiLanguage } from "../i18n";

interface CapacitySummaryProps {
    readonly capacity: CapacityTotals;
    readonly title: string;
    readonly compact?: boolean;
    readonly showTitle?: boolean;
    readonly powerLabel?: string;
    readonly powerTone?: "power" | "thermal";
}

interface ComponentPerformanceLineProps {
    readonly performance: ComponentPerformance;
}

interface Metric {
    readonly label: string;
    readonly value: string;
    readonly tone: CapacityTone;
}

type CapacityTone = "compute" | "gpu" | "iops" | "memory" | "network" | "power" | "storage" | "thermal";

interface CapacityLabels {
    readonly thermalLoad: string;
    readonly compute: string;
    readonly memory: string;
    readonly storage: string;
    readonly storageIops: string;
    readonly gpu: string;
    readonly gpuMemory: string;
    readonly inspection: string;
}

function capacityMetrics(
    capacity: CapacityTotals,
    labels: CapacityLabels,
    powerTone: "power" | "thermal",
): readonly Metric[] {
    const metrics: Metric[] = [{ label: labels.thermalLoad, value: formatPower(capacity.powerWatts), tone: powerTone }];
    if (capacity.vCpus > 0) {
        metrics.push({ label: labels.compute, value: `${formatCount(capacity.vCpus)} vCPU`, tone: "compute" });
    }
    if (capacity.memoryGb > 0) {
        metrics.push({ label: labels.memory, value: formatDataCapacity(capacity.memoryGb), tone: "memory" });
    }
    if (capacity.storageGb > 0) {
        metrics.push({ label: labels.storage, value: formatDataCapacity(capacity.storageGb), tone: "storage" });
    }
    if (capacity.storageIops > 0) {
        metrics.push({ label: labels.storageIops, value: formatCount(capacity.storageIops), tone: "iops" });
    }
    if (capacity.gpuCount > 0) {
        metrics.push({ label: labels.gpu, value: formatCount(capacity.gpuCount), tone: "gpu" });
    }
    if (capacity.gpuMemoryGb > 0) {
        metrics.push({ label: labels.gpuMemory, value: formatDataCapacity(capacity.gpuMemoryGb), tone: "gpu" });
    }
    if (capacity.inspectionGbps > 0) {
        metrics.push({ label: labels.inspection, value: formatRate(capacity.inspectionGbps), tone: "network" });
    }
    return metrics;
}

function componentMetrics(
    performance: ComponentPerformance,
    labels: Pick<CapacityLabels, "gpu" | "inspection"> & { readonly storageSuffix: string },
): readonly string[] {
    const metrics: string[] = [];
    if (performance.powerWatts !== null) {
        metrics.push(formatPower(performance.powerWatts));
    }
    if ((performance.vCpus ?? 0) > 0) {
        metrics.push(`${formatCount(performance.vCpus ?? 0)} vCPU`);
    }
    if ((performance.memoryGb ?? 0) > 0) {
        metrics.push(formatDataCapacity(performance.memoryGb ?? 0));
    }
    if ((performance.storageGb ?? 0) > 0) {
        metrics.push(`${formatDataCapacity(performance.storageGb ?? 0)} ${labels.storageSuffix}`);
    }
    if ((performance.gpuCount ?? 0) > 0) {
        metrics.push(`${formatCount(performance.gpuCount ?? 0)} ${labels.gpu}`);
    }
    if ((performance.forwardingGbps ?? 0) > 0) {
        metrics.push(formatRate(performance.forwardingGbps ?? 0));
    }
    if ((performance.inspectionGbps ?? 0) > 0) {
        metrics.push(`${formatRate(performance.inspectionGbps ?? 0)} ${labels.inspection}`);
    }
    return metrics;
}

export function CapacitySummary({
    capacity,
    title,
    compact = false,
    showTitle = true,
    powerLabel,
    powerTone = "thermal",
}: CapacitySummaryProps): React.JSX.Element {
    const { t } = useUiLanguage();
    const metrics = capacityMetrics(
        capacity,
        {
            thermalLoad: powerLabel ?? t("thermalLoad"),
            compute: t("computeCapacity"),
            memory: t("memoryCapacity"),
            storage: t("storageCapacity"),
            storageIops: t("storageIops"),
            gpu: t("gpuCapacity"),
            gpuMemory: t("gpuMemory"),
            inspection: t("inspectionCapacity"),
        },
        powerTone,
    );
    return (
        <section className={`capacity-summary${compact ? " is-compact" : ""}`} aria-label={title}>
            {showTitle ? <strong className="capacity-summary-title">{title}</strong> : null}
            <div className="capacity-metrics">
                {metrics.map((metric) => (
                    <span data-tone={metric.tone} key={metric.label}>
                        <small>{metric.label}</small>
                        <b>{metric.value}</b>
                    </span>
                ))}
            </div>
        </section>
    );
}

export function ComponentPerformanceLine({ performance }: ComponentPerformanceLineProps): React.JSX.Element | null {
    const { t } = useUiLanguage();
    const metrics = componentMetrics(performance, {
        gpu: t("gpuCapacity"),
        inspection: t("inspectionSuffix"),
        storageSuffix: t("storageSuffix"),
    });
    return metrics.length === 0 ? null : <span className="component-performance-line">{metrics.join(" · ")}</span>;
}
